/**
 * FRONT-3077: the pure decisions around binding the Pro Builder window to one app.
 */

/** What the app card and the app edit page need to decide whether to offer "Open in Pro Builder". */
export interface OpenInProBuilderInputs {
  flagOn: boolean;
  orgEnabled: boolean;
  isOwner: boolean;
  isEditor?: boolean;
  /** Server confirms the editor grant and Git availability for this app. */
  proBuilderCodeEditable?: boolean;
  canBuildApps: boolean;
  /** The listing's `kind`; absent means an app (older rows carry none). */
  kind: string | undefined;
  archived: boolean;
}

/** Owners keep the legacy doorway; code editors need the server-confirmed Git capability. */
export function showOpenInProBuilder(i: OpenInProBuilderInputs): boolean {
  const isApp = i.kind === undefined || i.kind === 'app';
  const confirmedEditor = i.isEditor === true && i.proBuilderCodeEditable === true;
  const mayEdit = i.isOwner || confirmedEditor;
  return i.flagOn && i.orgEnabled && mayEdit && i.canBuildApps && isApp && !i.archived;
}

/**
 * After a bind succeeds: whether the terminal session must be restarted. ONLY
 * when the server says it ended a running shell (`restarted`). The client's own
 * cached folder is no evidence: a stale cache (another tab switched app) would
 * make a differing folder look like a change and kill a terminal that is
 * already in the right folder. A window with no running shell simply opens in
 * the new folder when its terminal mounts.
 */
export function shouldRestartTerminal(restarted: boolean): boolean {
  return restarted === true;
}

/** The reason text the server puts on a folder whose name the sandbox rejects (`INVALID_NAME_REASON` in `src/pro-builder/publish.ts`; a test pins the two together). */
export const INVALID_NAME_REASON =
  'Folder names must start with a letter or number, use only letters, numbers, dots, dashes and underscores, ' +
  'and be at most 100 characters.';

/**
 * Whether the picker offers "Work on this" for a row. A blocked reason is
 * normally a reason not to PUBLISH (archived, stale copy, builder busy, link
 * replaced, app gone), never a reason not to WORK; only a folder name the
 * server cannot open (it answers 404) is closed.
 */
export function canWorkOnProject(p: { blockedReason: string | null }): boolean {
  return p.blockedReason !== INVALID_NAME_REASON;
}

export const SWITCH_APP_PUBLISHING_REASON = 'Wait until publishing finishes';
export const SWITCH_APP_SWITCHING_REASON = 'Wait until the app has switched';

/**
 * Why the header's controls that rebind the window are unavailable right now,
 * or null. ONE busy reason for the app switcher, New, the New view's Start and
 * Save: a running publish of the bound app ("publishing" — a publish that
 * failed with another app showing would otherwise be lost) wins over a bind
 * still in flight ("switching" — two switches would race, and Save pressed
 * before the switch lands would publish the OLD app). Each control is disabled
 * iff this is non-null, and shows it as its hint and description.
 */
export function switchAppBlockedReason(publishing: boolean, switching = false): string | null {
  if (publishing) return SWITCH_APP_PUBLISHING_REASON;
  return switching ? SWITCH_APP_SWITCHING_REASON : null;
}

export const SAVE_APP_TOOLTIP = "Save App — runs the app's build, stores it in your Apps and makes it live.";
export const SAVE_APP_NO_APP_REASON = 'Pick an app to work on first';
export const SAVE_APP_FOLDER_MISSING_REASON = "This app's folder is missing — pick another app.";

/**
 * The header's Save disc, as ONE decision (CLAUDE.md FRONT-1670): disabled iff
 * there is a reason, and the reason is the hint. While the bound app publishes
 * the disc spins and its hint is the publish's current progress message
 * (`progress`, e.g. "Running the build…"). Otherwise, in order: a bind in
 * flight (`switching`), no folder bound, a folder bound whose project row is
 * missing (`bound` false), and the bound app's own refusal to publish
 * (`blockedReason`: archived, someone else's, stale copy…). Else it is enabled
 * and its hint is `SAVE_APP_TOOLTIP`.
 */
export function saveAppGate(i: {
  /** The bound folder's project row exists. */
  bound: boolean;
  /** A folder is bound at all (a bound window whose row is missing is not the same as no app picked). */
  folderBound?: boolean;
  publishing: boolean;
  progress: string;
  switching?: boolean;
  blockedReason?: string | null;
}): { pending: boolean; disabledReason: string | null } {
  if (i.publishing) return { pending: true, disabledReason: i.progress };
  if (i.switching) return { pending: false, disabledReason: SWITCH_APP_SWITCHING_REASON };
  if (!i.bound) return { pending: false, disabledReason: i.folderBound ? SAVE_APP_FOLDER_MISSING_REASON : SAVE_APP_NO_APP_REASON };
  return { pending: false, disabledReason: i.blockedReason ?? null };
}

/** Why "Open in Pro Builder" is unavailable right now, or null: it rebinds and restarts the terminal, so any running publish blocks it. Disabled iff non-null; shown as hint AND description. */
export function openInProBuilderBlockedReason(anyPublishRunning: boolean): string | null {
  return anyPublishRunning ? SWITCH_APP_PUBLISHING_REASON : null;
}

/** The picker's row title: the app's title when the folder has an app, else the folder name. */
export function projectDisplayName(p: { name: string; app: { title: string } | null }): string {
  return p.app?.title || p.name;
}
